// ────────────────────────────────────────────────────────────────────────────
// SYNTHÈSE DE STOCK — ce que le stock VAUT et ce qui manque
//
// Deux pages posent exactement la même question au catalogue : la page Stock
// (« combien vaut ce que je détiens ? ») et la bande de stats de la page
// Articles. Tant que chacune recalculait sa somme dans son `useMemo`, rien ne
// garantissait qu'elles affichent le MÊME nombre — et deux chiffres qui
// divergent d'une page à l'autre ne sont plus des statistiques, juste du bruit.
//
// C'est donc ici, et uniquement ici, que se décide :
//   • ce qu'« en stock » veut dire  → article suivi, quantité positive
//   • ce qu'« en rupture » veut dire → article suivi, quantité ≤ 0
//   • ce que « sous le seuil » veut dire → 0 < quantité ≤ stockMin, seuil posé
//   • comment on valorise           → au coût d'achat NET HT
// ────────────────────────────────────────────────────────────────────────────

import { netUnitCostHt } from '@erp/domain/sales';

/** Maximum positive value representable by the persisted NUMERIC(14,3). */
export const MAX_STOCK_QUANTITY = 99_999_999_999.999;

/**
 * Quantités de stock : la base les stocke en NUMERIC(14,3), donc aucun solde
 * applicatif ne doit conserver davantage de décimales. Le recalage évite que
 * l'addition IEEE-754 de mouvements exacts (par ex. 0,1 + 0,2) expose un faux
 * solde comme 0,30000000000000004.
 */
export function normalizeStockQuantity(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/** Millimes : la devise a 3 décimales, toute somme flottante s'y recale. */
const round3 = normalizeStockQuantity;

/**
 * Ce qu'il faut savoir d'un article pour le valoriser. Sous-ensemble volontaire
 * d'`Article` : le domaine ne connaît pas les DTO de la couche data-access, et
 * une projection allégée (id + prix + seuil) suffit — c'est justement ce que
 * sert `IArticleRepository.listValuationRows()`.
 */
export interface StockValuationArticle {
  id: string;
  /** Un article non suivi est exclu de toute métrique de stock. */
  stockTrackingEnabled: boolean;
  /** Prix d'achat brut HT. Inconnu (≤ 0) = article non valorisable. */
  purchasePriceHt?: number | null;
  /** Remise fournisseur en % (0–100). */
  supplierDiscount?: number | null;
  /** Seuil d'alerte. 0 ou absent = pas de seuil configuré. */
  stockMin?: number | null;
}

export interface StockSummary {
  /**
   * Valorisation au coût d'achat NET HT des seules quantités POSITIVES.
   *
   * Au coût et pas au prix de vente : c'est la seule lecture défendable de
   * « valeur du stock » (ce que la marchandise a coûté, pas ce qu'on espère en
   * tirer). Et sur les positifs seuls : une quantité négative est une anomalie
   * de saisie, la faire *retrancher* de la valorisation ferait passer une
   * erreur de stock pour de l'argent en moins.
   */
  totalValueHt: number;
  /** Articles détenus (quantité > 0). */
  inStockCount: number;
  /** Articles suivis à zéro ou en négatif — « qu'est-ce qui manque ? ». */
  outOfStockCount: number;
  /** Articles encore détenus mais retombés à leur seuil d'alerte ou en dessous. */
  belowMinCount: number;
  /**
   * Articles détenus SANS prix d'achat connu. Ils comptent pour 0 DT dans
   * `totalValueHt` : la valorisation est donc un plancher, pas une estimation.
   * Exposé pour que l'UI puisse le dire plutôt que de laisser croire à un
   * total exhaustif.
   */
  unvaluedInStockCount: number;
}

const EMPTY: StockSummary = {
  totalValueHt: 0,
  inStockCount: 0,
  outOfStockCount: 0,
  belowMinCount: 0,
  unvaluedInStockCount: 0,
};

/**
 * Synthétise le stock du catalogue.
 *
 * `quantityByArticleId` porte les quantités agrégées tous dépôts confondus
 * (`IStockRepository.getAllStock()` réduit par article). Un article suivi et
 * absent du registre vaut 0. Un article non suivi est exclu : cette distinction
 * ne peut pas être déduite de `stockMin` ni de l'absence de mouvements.
 *
 * @param articles Le catalogue à synthétiser (entier, ou déjà restreint par
 *                 l'appelant — la fonction ne filtre rien d'elle-même).
 */
export function summarizeStock(
  articles: readonly StockValuationArticle[] | null | undefined,
  quantityByArticleId: ReadonlyMap<string, number>,
): StockSummary {
  if (!articles || articles.length === 0) return { ...EMPTY };

  let totalValueHt = 0;
  let inStockCount = 0;
  let outOfStockCount = 0;
  let belowMinCount = 0;
  let unvaluedInStockCount = 0;

  for (const article of articles) {
    if (article.stockTrackingEnabled !== true) continue;
    const raw = quantityByArticleId.get(article.id);
    const quantity = Number.isFinite(raw) ? (raw as number) : 0;

    if (quantity <= 0) {
      outOfStockCount += 1;
      continue;
    }

    inStockCount += 1;

    const unitCost = netUnitCostHt(article);
    if (unitCost === null) {
      unvaluedInStockCount += 1;
    } else {
      totalValueHt += quantity * unitCost;
    }

    const stockMin = Number(article.stockMin) || 0;
    if (stockMin > 0 && quantity <= stockMin) {
      belowMinCount += 1;
    }
  }

  return {
    totalValueHt: round3(totalValueHt),
    inStockCount,
    outOfStockCount,
    belowMinCount,
    unvaluedInStockCount,
  };
}

/**
 * Réduit le relevé de stock par dépôt en « quantité totale par article ».
 *
 * Le relevé sort du repository à la maille article × dépôt ; toutes les mesures
 * de cette page sont, elles, à la maille article. Un helper partagé plutôt
 * qu'un `reduce` recopié à chaque appel : la page Stock et la page Articles
 * doivent additionner les dépôts de la même façon.
 */
export function totalQuantityByArticle(
  levels: readonly { articleId: string; quantity: number }[] | null | undefined,
): Map<string, number> {
  const map = new Map<string, number>();
  for (const level of levels ?? []) {
    const quantity = Number(level.quantity) || 0;
    map.set(
      level.articleId,
      normalizeStockQuantity((map.get(level.articleId) ?? 0) + quantity),
    );
  }
  return map;
}

/** Solde par variante, à partir des matrices chargées pour un document. */
export function variantStockById(
  matrices: Readonly<
    Record<string, { variants: readonly { id: string; stockQuantity: number }[] }>
  > | null | undefined,
): Map<string, number> {
  const map = new Map<string, number>();
  for (const matrix of Object.values(matrices ?? {})) {
    for (const variant of matrix.variants) {
      map.set(variant.id, Number(variant.stockQuantity) || 0);
    }
  }
  return map;
}

/**
 * Stock à afficher sur une ligne de document de vente ou d'achat.
 *
 * Une ligne qui nomme une variante vend — ou reçoit — CETTE variante, donc
 * c'est son solde qu'il faut lire, jamais le total de l'article : le parent
 * additionne toutes ses variantes et ferait passer pour disponible une taille
 * en rupture.
 *
 * `undefined` = pas encore connu (matrice en cours de chargement, variante
 * disparue). L'appelant affiche un tiret plutôt que de retomber sur le chiffre
 * du parent : un chiffre juste qui arrive après un chiffre faux se lit comme
 * un bug, et c'est le faux que le vendeur aura mémorisé.
 */
export function documentLineStockQuantity(
  line: { articleId?: string | null; variantId?: string | null },
  stock: {
    byArticle: ReadonlyMap<string, number>;
    byVariant: ReadonlyMap<string, number>;
  },
): number | undefined {
  if (!line.articleId) return undefined;
  if (line.variantId) return stock.byVariant.get(line.variantId);
  return stock.byArticle.get(line.articleId) ?? 0;
}
